'use client';

import { useMemo } from 'react';

import { useGetWorkspaceMembersQuery } from '@/features/workspaces/api/workspacesApi';
import { SmartInput } from '@web/ui';

import styles from './JobTeamSection.module.scss';

export type JobTeamSectionProps = {
  jobLeadId: string;
  teamUserIds: string[];
  fieldError?: string;
  onJobLeadChange: (userId: string) => void;
  onTeamUserIdsChange: (userIds: string[]) => void;
  readOnly?: boolean;
  teamMembersReadOnly?: boolean;
  showHeader?: boolean;
  showJobLead?: boolean;
  showTeamMembers?: boolean;
};

function toMemberLabel(member: {
  user: { first_name: string; last_name: string; email: string };
}) {
  const fullName =
    `${member.user.first_name ?? ''} ${member.user.last_name ?? ''}`.trim();
  return fullName || member.user.email;
}

export function JobTeamSection({
  jobLeadId,
  teamUserIds,
  fieldError,
  onJobLeadChange,
  onTeamUserIdsChange,
  readOnly = false,
  teamMembersReadOnly = false,
  showHeader = true,
  showJobLead = true,
  showTeamMembers = true,
}: JobTeamSectionProps) {
  const { data, isFetching } = useGetWorkspaceMembersQuery({
    status: 'active',
    page_size: 100,
  });

  const memberOptions = useMemo(() => {
    const members = data?.data?.results ?? [];
    return members.map((member) => ({
      value: member.user.id,
      label: toMemberLabel(member),
    }));
  }, [data?.data?.results]);

  return (
    <section className={styles.jobTeamSection}>
      {showHeader ? (
        <>
          <h3 className={styles.jobTeamSection__title}>Team</h3>
          <p className={styles.jobTeamSection__description}>
            Choose the internal lead and the team members who should be
            associated with this job.
          </p>
        </>
      ) : null}

      {showJobLead ? (
        <SmartInput
          type="select"
          name="job-lead-id"
          label="Job Lead"
          size="small"
          value={jobLeadId || ''}
          error={fieldError}
          options={[
            { value: '', label: 'Select a job lead' },
            ...memberOptions,
          ]}
          readOnly={readOnly || isFetching}
          onChange={(value) => onJobLeadChange(String(value))}
        />
      ) : null}

      {showTeamMembers ? (
        <div className={styles.jobTeamSection__members}>
          <span className={styles.jobTeamSection__membersLabel}>
            Team Members
          </span>
          <div className={styles.jobTeamSection__membersGrid}>
            {memberOptions.map((option) => (
              <div
                key={option.value}
                className={styles.jobTeamSection__memberItem}
              >
                <SmartInput
                  type="checkbox"
                  name={`job-team-user-${option.value}`}
                  label={option.label}
                  checked={teamUserIds.includes(option.value)}
                  readOnly={readOnly || teamMembersReadOnly || isFetching}
                  onChange={(checked) => {
                    if (checked) {
                      if (!teamUserIds.includes(option.value)) {
                        onTeamUserIdsChange([...teamUserIds, option.value]);
                      }
                      return;
                    }

                    onTeamUserIdsChange(
                      teamUserIds.filter((id) => id !== option.value),
                    );
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
